import { Box, Checkbox, Chip, ListItemButton, Paper, Stack, Typography, useTheme } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CaseResponseDto } from '@bandido/shared/dtos';
import { getTimeSinceDate } from '@frontend/features/common/helper';
import { routes } from '@frontend/router/routes';
import type { CaseListCustomColumn } from '../hooks/useCaseListColumns';

export const CaseMobileCard = ({
  item,
  customColumns = [],
  selection,
}: {
  item: CaseResponseDto;
  customColumns?: CaseListCustomColumn[];
  selection?: { checked: boolean; disabled: boolean; onChange: (checked: boolean) => void };
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  return (
    <Paper
      component="li"
      variant="outlined"
      key={item.id}
      sx={{ minWidth: 0, borderRadius: 2, overflowWrap: 'anywhere', position: 'relative', borderColor: selection?.checked ? 'primary.main' : 'divider' }}
    >
      {selection && <Checkbox checked={selection.checked} disabled={selection.disabled}
        inputProps={{ 'aria-label': t('cases.list.bulk.selectCase', { id: item.id }) }}
        onChange={(_, checked) => selection.onChange(checked)}
        sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1, width: 44, height: 44 }} />}
      <ListItemButton
        component={RouterLink}
        to={routes.dashboard.caseView.replace(':id', String(item.id))}
        sx={{ display: 'block', p: 2, borderRadius: 2 }}
      >
        <Stack direction="row" gap={1} alignItems="flex-start" mb={1} sx={selection ? { pl: 5, minHeight: 28 } : undefined}>
          <Typography fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
            {item.name}
          </Typography>
          <ChevronRight color="action" />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          #{item.id} · {[item.clientFirstName, item.clientLastName].filter(Boolean).join(' ') || t('common.unassigned')}
        </Typography>
        <Chip
          size="small"
          label={t(`cases.status.${item.status}`)}
          sx={{
            my: 1.5,
            maxWidth: '100%',
            bgcolor: theme.palette.cases[item.status]?.bg,
            color: theme.palette.cases[item.status]?.text,
            height: 'auto',
            minHeight: 24,
            '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
          }}
        />
        <Box display="grid" gridTemplateColumns="minmax(0, 1fr) minmax(0, 2fr)" gap={0.75}>
          {[
            ['step', item.stepName],
            ['workflow', item.workflowName],
            ['agent', [item.agentFirstName, item.agentLastName].filter(Boolean).join(' ')],
            ['channel', item.originatedByChannel ? t(`cases.originatedByChannel.${item.originatedByChannel}`) : ''],
          ].map(([key, value]) => (
            <Box key={key} sx={{ display: 'contents' }}>
              <Typography variant="caption" color="text.secondary">
                {t(`common.${key}`)}
              </Typography>
              <Typography variant="caption">{value || t('common.unassigned')}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
          {t('common.updated')} {getTimeSinceDate(item.updatedAt)}
        </Typography>
      </ListItemButton>
      {customColumns.length > 0 && (
        <Box px={2} pb={2}>
          {customColumns.map((column) => (
            <Box key={column.id} display="flex" flexWrap="wrap" gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                {column.label}:
              </Typography>
              <Typography component="div" variant="caption" sx={{ minWidth: 0, maxWidth: '100%' }}>
                {column.render(item.id)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};
